import {
	TypeVisual,
	TypeABCD,
	TypeAssociation,
	TypeConnection,
	TypeStandard,
} from '@quizzach/assets';
import { IPremadeQuiz, QuizMode, StepRouteMap } from './Quiz.interface';

export enum QuestionType {
	standard = 'standard',
	connection = 'connection',
	association = 'association',
	abcd = 'abcd',
	visual = 'visual',
	estimation = 'estimation',
}

export interface IQuiz {
	uuid: string;
	quiz_type: 'generator' | 'quick' | 'premade' | '';
	created_at?: string;
	questions: IQuestion[];
	fresh?: boolean;
	number_of_questions: number;
	premade_quiz?: IPremadeQuiz;
}

export class IQuiz {
	constructor(object?: Partial<IQuiz>) {
		this.uuid = object?.uuid || '';
		this.quiz_type = object?.quiz_type || '';
		this.created_at = object?.created_at || '';
		this.questions = object?.questions || [new IQuestion()];
		this.fresh = object?.fresh || false;
		this.number_of_questions = object?.number_of_questions || 0;
		this.premade_quiz = object?.premade_quiz || undefined;
	}
}

export interface IQuestion {
	uuid: string;
	type:
		| QuestionType.standard
		| QuestionType.association
		| QuestionType.connection
		| QuestionType.visual
		| QuestionType.abcd
		| QuestionType.estimation
		| null;
	points: string;
	level: number;

	points_explanation?: string;
	country_specific: boolean;
	standard_question?: IStandardQuestion;
	association_question?: IAssociation;
	abcd_question?: IAbcdQuestion;
	connection_question?: IConnectionQuestion;
	visual_question?: IVisualQuestion;
	estimation_question?: IEstimationQuestion;
	created_at?: string;
	updated_at?: string;
	deleted_at?: string;
	long_question?: boolean;
}

export class IQuestion {
	constructor(object?: Partial<IQuestion>) {
		this.type = object?.type || null;
		this.points = object?.points || '';
		this.points_explanation = object?.points_explanation || '';
		this.country_specific = object?.country_specific || false;
		this.level = object?.level || 0;
		this.standard_question = new IStandardQuestion(object?.standard_question);
		this.association_question = new IAssociation(object?.association_question);
		this.abcd_question = new IAbcdQuestion(object?.abcd_question);
		this.connection_question = new IConnectionQuestion(
			object?.connection_question
		);
		this.visual_question = new IVisualQuestion(object?.visual_question);
		this.estimation_question = new IEstimationQuestion(
			object?.estimation_question
		);
		this.created_at = object?.created_at || '';
		this.updated_at = object?.updated_at || '';
		this.deleted_at = object?.deleted_at || '';
		this.long_question = object?.long_question || false;
	}
}

interface IStandardQuestion {
	question_hr: string;
	answer_hr: string;
	explanation_hr?: string;
}

class IStandardQuestion {
	constructor(object?: Partial<IStandardQuestion>) {
		this.question_hr = object?.question_hr || '';
		this.answer_hr = object?.answer_hr || '';
		this.explanation_hr = object?.explanation_hr || '';
	}
}

export interface IAssociation {
	a1: string;
	a2: string;
	a3: string;
	a4: string;
	b1: string;
	b2: string;
	b3: string;
	b4: string;
	c1: string;
	c2: string;
	c3: string;
	c4: string;
	d1: string;
	d2: string;
	d3: string;
	d4: string;
	a: string;
	b: string;
	c: string;
	d: string;
	answer: string;
	explanation?: string;
}

export class IAssociation {
	constructor(object?: Partial<IAssociation>) {
		this.a1 = object?.a1 || '';
		this.a2 = object?.a2 || '';
		this.a3 = object?.a3 || '';
		this.a4 = object?.a4 || '';
		this.b1 = object?.b1 || '';
		this.b2 = object?.b2 || '';
		this.b3 = object?.b3 || '';
		this.b4 = object?.b4 || '';
		this.c1 = object?.c1 || '';
		this.c2 = object?.c2 || '';
		this.c3 = object?.c3 || '';
		this.c4 = object?.c4 || '';
		this.d1 = object?.d1 || '';
		this.d2 = object?.d2 || '';
		this.d3 = object?.d3 || '';
		this.d4 = object?.d4 || '';
		this.a = object?.a || '';
		this.b = object?.b || '';
		this.c = object?.c || '';
		this.d = object?.d || '';
		this.answer = object?.answer || '';
		this.explanation = object?.explanation || '';
	}
}
interface IAbcdQuestion {
	text: string;
	a: string;
	b: string;
	c: string;
	d: string;
	answer: string;
	explanation?: string;
	abc_question?: boolean;
}

class IAbcdQuestion {
	constructor(object?: Partial<IAbcdQuestion>) {
		this.text = object?.text || '';
		this.a = object?.a || '';
		this.b = object?.b || '';
		this.c = object?.c || '';
		this.d = object?.d || '';
		this.answer = object?.answer || '';
		this.explanation = object?.explanation || '';
		this.abc_question = object?.abc_question || false;
	}
}

interface IConnectionQuestion {
	text: string;
	a: string;
	b: string;
	c: string;
	d: string;
	n1: string;
	n2: string;
	n3: string;
	n4: string;
	answer_a: string;
	answer_b: string;
	answer_c: string;
	answer_d: string;
	explanation?: string;
}

class IConnectionQuestion {
	constructor(object?: Partial<IConnectionQuestion>) {
		this.text = object?.text || '';
		this.a = object?.a || '';
		this.b = object?.b || '';
		this.c = object?.c || '';
		this.d = object?.d || '';
		this.n1 = object?.n1 || '';
		this.n2 = object?.n2 || '';
		this.n3 = object?.n3 || '';
		this.n4 = object?.n4 || '';
		this.answer_a = object?.answer_a || '';
		this.answer_b = object?.answer_b || '';
		this.answer_c = object?.answer_c || '';
		this.answer_d = object?.answer_d || '';
		this.explanation = object?.explanation || '';
	}
}

interface IVisualQuestion {
	answer: string;
	explanation: string;
	orientation: 'landscape' | 'portrait';
	text: string;
	filename: string;
	// Optional alternative image for the answer page (e.g. "revealed" version).
	answer_filename?: string;
	a?: string;
	b?: string;
	c?: string;
	d?: string;
	licence_needed?: boolean;
	original_photo_url?: string;
	licence_name?: string;
	licence_link?: string;
	photo_author_name?: string;
	is_cropped?: boolean;
}

class IVisualQuestion {
	constructor(object?: Partial<IVisualQuestion>) {
		this.answer = object?.answer || '';
		this.explanation = object?.explanation || '';
		this.orientation = object?.orientation || 'landscape';
		this.text = object?.text || '';
		this.filename = object?.filename || '';
		this.answer_filename = object?.answer_filename || '';
		this.a = object?.a || '';
		this.b = object?.b || '';
		this.c = object?.c || '';
		this.d = object?.d || '';
		this.licence_needed = object?.licence_needed || false;
		this.original_photo_url = object?.original_photo_url || '';
		this.licence_name = object?.licence_name || '';
		this.licence_link = object?.licence_link || '';
		this.photo_author_name = object?.photo_author_name || '';
		this.is_cropped = object?.is_cropped || false;
	}
}

interface IEstimationQuestion {
	text: string;
	answer: string;
	explanation: string;
}

class IEstimationQuestion {
	constructor(object?: Partial<IEstimationQuestion>) {
		this.text = object?.text || '';
		this.answer = object?.answer || '';
		this.explanation = object?.explanation || '';
	}
}

// `text` holds translation keys; resolve with t(item.text) at the rendering site
export const quickQuestionTypes = [
	{
		type: 'standard',
		src: TypeStandard,
		alt: 'standard',
		text: 'quiz.questionTypes.standard',
	},
	{
		type: 'abcd',
		src: TypeABCD,
		alt: 'abcd',
		text: 'quiz.questionTypes.abcd',
	},
	{
		type: 'connection',
		src: TypeConnection,
		alt: 'connection',
		text: 'quiz.questionTypes.connection',
	},
	{
		type: 'association',
		src: TypeAssociation,
		alt: 'association',
		text: 'quiz.questionTypes.association',
	},
	{
		type: 'visual',
		src: TypeVisual,
		alt: 'visual',
		text: 'quiz.questionTypes.visual',
	},
];

export interface QuizOutletContextType {
	quiz: IQuiz;
	premade_quiz?: string;
	questions: IQuestion[];
	quizType: string;
	halftime: boolean;
	halfLength: number;
	mode: QuizMode;
	currentQuestionNumber: number;
	error: boolean;
	handleSetHalftime: (value: boolean) => void;
	handleSetMode: (mode: QuizMode) => void;
	handleNext: VoidFunction;
	handlePrevious: VoidFunction;
	setFeedback: VoidFunction;
	setUrlNum: any;
	setQuizNavigationMap: React.Dispatch<
		React.SetStateAction<StepRouteMap | undefined>
	>;
	quizNavigationMap: StepRouteMap;
	setNavigationType: React.Dispatch<React.SetStateAction<any>>;
}
